import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, User, Printer, Download } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { API_URL } from "../config";

const hexToRgba = (hex, alpha = 1) => {
  if (!hex) return `rgba(245,158,11,${alpha})`;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TicketView = () => {
  const { ticketNumber } = useParams();
  const [ticket, setTicket] = useState(null);
  const [show, setShow] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const ticketRef = useRef(null);

  useEffect(() => {
    fetchTicket();
  }, [ticketNumber]);

  const fetchTicket = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ticket/${ticketNumber}`);
      setTicket(response.data.ticket);
      setShow(response.data.show);
      setSession(response.data.session);
    } catch (err) {
      setError("Bilhete não encontrado");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleSavePDF = async () => {
    if (!ticketRef.current) return;
    try {
      const canvas = await html2canvas(ticketRef.current, {
        backgroundColor: "#18181b",
        scale: 2,
        useCORS: true,
        scrollY: -window.scrollY,
        windowHeight: ticketRef.current.scrollHeight,
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (imgHeight <= pdfHeight - 20) {
        // Fits in one page - center vertically
        const yOffset = (pdfHeight - imgHeight) / 2;
        pdf.setFillColor(24, 24, 27);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(imgData, "PNG", 10, yOffset, imgWidth, imgHeight);
      } else {
        // Scale down to fit one page
        const scale = (pdfHeight - 20) / imgHeight;
        const scaledWidth = imgWidth * scale;
        const scaledHeight = imgHeight * scale;
        const xOffset = (pdfWidth - scaledWidth) / 2;
        pdf.setFillColor(24, 24, 27);
        pdf.rect(0, 0, pdfWidth, pdfHeight, "F");
        pdf.addImage(imgData, "PNG", xOffset, 10, scaledWidth, scaledHeight);
      }
      
      pdf.save(`bilhete-${ticket?.ticket_number || "ticket"}.pdf`);
    } catch (err) {
      console.error("PDF error:", err);
      toast.error("Erro ao gerar PDF");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#f59e0b', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center" data-testid="ticket-error">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-4">Oops!</h1>
          <p className="text-zinc-500">{error}</p>
          <a href="/" className="mt-4 inline-block" style={{ color: '#f59e0b' }}>Voltar ao início</a>
        </div>
      </div>
    );
  }

  const ac = show?.accent_color || "#f59e0b";
  const badge = show?.hero_badge || "";
  const qrData = `TICKET:${ticket?.ticket_number}|NAME:${ticket?.name}|SESSION:${session?.date} ${session?.time}`;

  const customData = ticket?.custom_data || {};
  const customFields = Object.entries(customData).filter(
    ([, val]) => val && String(val).trim() && !["true", "false"].includes(String(val).toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#09090b] py-12 px-6" data-testid="ticket-view-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md mx-auto"
      >
        {/* Ticket Card */}
        <div
          ref={ticketRef}
          className="rounded-2xl overflow-hidden print-ticket"
          style={{ background: "linear-gradient(135deg, #18181b 0%, #27272a 100%)", border: `1px solid ${hexToRgba(ac, 0.3)}` }}
          data-testid="ticket-container"
        >
          {/* Header */}
          <div className="text-center p-6" style={{ borderBottom: `2px dashed ${ac}` }}>
            <h1 className="text-2xl font-bold mb-1" style={{ color: ac }}>{show?.name}</h1>
            {badge && <p className="text-zinc-400 text-xs uppercase tracking-widest">{badge}</p>}
          </div>

          {/* Details */}
          <div className="p-6 space-y-0">
            <div className="flex items-center gap-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <User className="w-5 h-5 shrink-0" style={{ color: ac }} />
              <div className="flex-1">
                <p className="text-zinc-500 text-xs">Nome</p>
                <p className="text-white font-medium">{ticket?.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Calendar className="w-5 h-5 shrink-0" style={{ color: ac }} />
              <div className="flex-1">
                <p className="text-zinc-500 text-xs">Data</p>
                <p className="text-white font-medium">{session?.date}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <Clock className="w-5 h-5 shrink-0" style={{ color: ac }} />
              <div className="flex-1">
                <p className="text-zinc-500 text-xs">Horário</p>
                <p className="text-white font-medium">{session?.time}</p>
              </div>
            </div>

            <div className="flex items-center gap-4 py-3" style={{ borderBottom: customFields.length > 0 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
              <MapPin className="w-5 h-5 shrink-0" style={{ color: ac }} />
              <div className="flex-1">
                <p className="text-zinc-500 text-xs">Local</p>
                <p className="text-white font-medium">{show?.location}</p>
              </div>
            </div>

            {customFields.map(([key, val], idx) => (
              <div key={key} className="flex items-center gap-4 py-3" style={{ borderBottom: idx < customFields.length - 1 ? '1px solid rgba(255,255,255,0.06)' : 'none' }}>
                <div className="w-5 h-5 shrink-0" />
                <div className="flex-1">
                  <p className="text-zinc-500 text-xs">{key}</p>
                  <p className="text-white font-medium">{val}</p>
                </div>
              </div>
            ))}
          </div>

          {/* QR Code */}
          <div className="text-center p-6" style={{ borderTop: `2px dashed ${ac}` }}>
            <p className="text-zinc-400 text-sm mb-4">Apresente este QR Code na entrada</p>
            <div className="bg-white p-4 rounded-xl inline-block">
              <QRCodeSVG
                value={qrData}
                size={180}
                level="H"
                includeMargin={false}
                data-testid="ticket-qr-code"
              />
            </div>
            <div className="mt-4">
              <p className="text-zinc-500 text-xs">Este bilhete é pessoal e intransmissível</p>
              <p className="text-xs mt-1" style={{ color: ac }}>&copy; BILHETEZ</p>
            </div>
          </div>
        </div>

        {/* Action Buttons - hidden in print */}
        <div className="flex gap-3 mt-6 no-print">
          <Button
            onClick={handlePrint}
            className="flex-1 py-5 font-semibold text-black"
            style={{ backgroundColor: ac }}
            data-testid="print-ticket-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            Imprimir
          </Button>
          <Button
            onClick={handleSavePDF}
            className="flex-1 py-5 font-semibold border"
            style={{ borderColor: ac, color: ac, backgroundColor: 'transparent' }}
            data-testid="save-pdf-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Guardar PDF
          </Button>
        </div>

        <div className="text-center mt-6 no-print">
          <a href="/" className="text-zinc-500 hover:opacity-80 transition-colors text-sm" style={{ "--hover-color": ac }}>
            ← Voltar ao site
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default TicketView;
